import React, { useEffect, useMemo, useState } from "react";
import {
	Alert,
	Box,
	Button,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	Divider,
	Stack,
	TextField,
	Typography,
} from "@mui/material";
import moment from "moment";
import { withoutSymbolFormatter } from "utilities/currencyFormatter";

const money = (value) => `UGX ${withoutSymbolFormatter(Number(value || 0))}`;

/**
 * Issue an EFRIS e-invoice for N months of rent (VAT-registered landlords).
 * The landlord is the seller and the tenant the buyer; VAT is included in the rent.
 */
export default function EfrisPeriodInvoiceDialog({
	open,
	onClose,
	tenancy,
	vatRate = "0.18",
	defaultStart,
	isSubmitting,
	onIssue,
}) {
	const [start, setStart] = useState("");
	const [months, setMonths] = useState(1);
	const [rent, setRent] = useState("");
	const [error, setError] = useState("");
	const [issued, setIssued] = useState(null);

	useEffect(() => {
		if (open) {
			setStart(moment(defaultStart || tenancy?.suggested_start || undefined).format("YYYY-MM-DD"));
			setMonths(1);
			setRent(tenancy?.monthly_rent ? String(Number(tenancy.monthly_rent)) : "");
			setError("");
			setIssued(null);
		}
	}, [open, defaultStart, tenancy]);

	const preview = useMemo(() => {
		const rate = Number(vatRate) || 0;
		const gross = (Number(rent) || 0) * (Number(months) || 0);
		const vat = Math.round(((gross * rate) / (1 + rate)) * 100) / 100;
		const end = start
			? moment(start).add(Number(months) || 0, "months").subtract(1, "day")
			: null;
		return { gross, vat, net: gross - vat, end };
	}, [rent, months, start, vatRate]);

	const valid = start && Number(months) >= 1 && Number(months) <= 24 && Number(rent) > 0;

	const handleIssue = async () => {
		setError("");
		const result = await onIssue({
			occupancy_id: tenancy?.occupancy_id,
			period_start: start,
			months: Number(months),
		});
		if (result.success) setIssued(result.data?.data);
		else setError(result.message);
	};

	return (
		<Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
			<DialogTitle>
				<Typography variant="h4">Generate EFRIS invoice</Typography>
				{tenancy && (
					<Typography variant="body2" color="text.secondary">
						{tenancy.tenant_name} · {tenancy.property_name} {tenancy.unit_name}
					</Typography>
				)}
			</DialogTitle>
			<DialogContent dividers>
				{issued ? (
					<Alert severity="success">
						EFRIS invoice issued. FDN <strong>{issued.fiscal_document_number}</strong> for{" "}
						{moment(issued.period_start).format("D MMM YYYY")} –{" "}
						{moment(issued.period_end).format("D MMM YYYY")}, {money(issued.amount)}.
					</Alert>
				) : (
					<Stack spacing={2}>
						<Typography variant="body2">
							Issues a URA EFRIS e-invoice in the landlord's name for the rent below. Payments
							made for these months won't get a separate EFRIS receipt, so the rent is only
							reported once.
						</Typography>
						<Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
							<TextField
								label="Start date"
								type="date"
								size="small"
								value={start}
								onChange={(e) => setStart(e.target.value)}
								InputLabelProps={{ shrink: true }}
							/>
							<TextField
								label="Months"
								type="number"
								size="small"
								value={months}
								inputProps={{ min: 1, max: 24 }}
								onChange={(e) => setMonths(e.target.value)}
								sx={{ width: 110 }}
							/>
							<TextField
								label="Monthly rent (UGX, VAT incl.)"
								size="small"
								value={rent ? withoutSymbolFormatter(Number(rent)) : ""}
								InputProps={{ readOnly: true }}
								helperText="From the tenancy's rent; can't be edited"
							/>
						</Stack>
						<Divider />
						<Box>
							{preview.end && (
								<Typography variant="body2" sx={{ mb: 1 }}>
									Period: {moment(start).format("D MMM YYYY")} – {preview.end.format("D MMM YYYY")}
								</Typography>
							)}
							<Stack direction="row" justifyContent="space-between">
								<Typography variant="body2">
									Rent {months || 0} × {money(rent)}
								</Typography>
								<Typography variant="body2">{money(preview.gross)}</Typography>
							</Stack>
							<Stack direction="row" justifyContent="space-between">
								<Typography variant="body2" color="text.secondary">
									Net of VAT
								</Typography>
								<Typography variant="body2" color="text.secondary">
									{money(preview.net)}
								</Typography>
							</Stack>
							<Stack direction="row" justifyContent="space-between">
								<Typography variant="body2" color="text.secondary">
									VAT {Number(vatRate) * 100}% (included)
								</Typography>
								<Typography variant="body2" color="text.secondary">
									{money(preview.vat)}
								</Typography>
							</Stack>
							<Stack direction="row" justifyContent="space-between" sx={{ mt: 1 }}>
								<Typography variant="h6">Invoice total</Typography>
								<Typography variant="h6">{money(preview.gross)}</Typography>
							</Stack>
						</Box>
						{error && <Alert severity="error">{error}</Alert>}
					</Stack>
				)}
			</DialogContent>
			<DialogActions>
				<Button onClick={onClose}>{issued ? "Close" : "Cancel"}</Button>
				{!issued && (
					<Button variant="contained" disabled={!valid || isSubmitting} onClick={handleIssue}>
						{isSubmitting ? "Issuing…" : "Issue EFRIS invoice"}
					</Button>
				)}
			</DialogActions>
		</Dialog>
	);
}
